import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useResetPassword } from "../../hooks/useResetPassword";
import {
  createResetPasswordSchema,
  ResetPasswordFormData,
} from "../../helpers/userValidation";
import PasswordRequirements from "../common/PasswordRequirements";

const ResetPasswordForm: React.FC = () => {
  const { t } = useTranslation();
  // Use the custom hook which encapsulates the API call and toast feedback
  const { handleResetPassword, isLoading, isSuccess } = useResetPassword();

  const [params, setParams] = useState<{ key: string; login: string } | null>(
    null
  );

  // Extract Query Params on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const key = urlParams.get("key");
    const login = urlParams.get("login");
    if (key && login) {
      setParams({ key, login });
    }
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(createResetPasswordSchema(t)),
  });

  const passwordValue = watch("password");

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!params) return;
    await handleResetPassword({
      key: params.key,
      login: params.login,
      password: data.password,
    });
  };

  if (!params) {
    return (
      <div className="alert alert--error">
        {t("user.resetPassword.invalidLink", "Invalid or missing reset link.")}
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="auth-success-message">
        <h3>{t("user.resetPassword.successTitle", "Password Reset!")}</h3>
        <p>
          {t(
            "user.resetPassword.successMessage",
            "Your password has been updated successfully."
          )}
        </p>
        <a
          href={window.commonVariables?.loginUrl}
          className="button button--primary"
        >
          {t("user.auth.loginButton", "Log In")}
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
      <h2>{t("user.resetPassword.title", "Set New Password")}</h2>

      <div className="form-field">
        <label htmlFor="new-password">
          {t("user.accountDetails.newPassword")}
        </label>
        <input
          id="new-password"
          type="password"
          {...register("password")}
          className={errors.password ? "has-error" : ""}
        />
        <PasswordRequirements
          password={passwordValue}
          isVisible={!!passwordValue}
        />
        {errors.password && (
          <p className="form-field__error">{errors.password.message}</p>
        )}
      </div>

      <div className="form-field">
        <label htmlFor="confirm-password">
          {t("user.accountDetails.confirmPassword")}
        </label>
        <input
          id="confirm-password"
          type="password"
          {...register("password_confirm")}
          className={errors.password_confirm ? "has-error" : ""}
        />
        {errors.password_confirm && (
          <p className="form-field__error">{errors.password_confirm.message}</p>
        )}
      </div>

      <button type="submit" disabled={isLoading}>
        {isLoading
          ? t("user.resetPassword.processing")
          : t("user.resetPassword.submit", "Save Password")}
      </button>
    </form>
  );
};

export default ResetPasswordForm;
